// src/components/ContactModal/index.js
//
// Modal de contato no padrão Aeros Dark (mockup "Modal Contato A"): avatar +
// Nome/WhatsApp, E-mail/Carteira, Tags, Informações adicionais, toggles e
// rodapé com "Bloquear contato". A lógica de salvar (contato, carteira,
// tags) é a mesma da versão anterior.
import React, { useState, useEffect, useRef, useContext } from "react";
import { format } from "date-fns";
import * as Yup from "yup";
import { Formik, FieldArray, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles, alpha } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import IconButton from "@material-ui/core/IconButton";
import CircularProgress from "@material-ui/core/CircularProgress";
import Switch from "@material-ui/core/Switch";
import MenuItem from "@material-ui/core/MenuItem";
import InputAdornment from "@material-ui/core/InputAdornment";
import Autocomplete from "@material-ui/lab/Autocomplete";
import CloseIcon from "@material-ui/icons/Close";
import AddIcon from "@material-ui/icons/Add";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import BlockIcon from "@material-ui/icons/Block";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import PhotoCameraOutlinedIcon from "@material-ui/icons/PhotoCameraOutlined";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import useAerosModalStyles from "../../styles/aerosModalStyles";
import useAerosSettingsStyles from "../Settings/aerosSettingsStyles";
import { AEROS, stateText, contrastTextFor } from "../../styles/aerosTokens";
import AerosConfirmModal from "../AerosConfirmModal";
import { initialsOf, avatarColorFor, formatPhone } from "../../pages/Contacts/contactRules";

const useStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";
  const borderInput = isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.1)";
  return {
    paper: { width: 600, maxWidth: "calc(100vw - 32px)", maxHeight: "calc(var(--vh-full) - 48px)", margin: 16 },
    topRow: { display: "flex", alignItems: "center", gap: 14 },
    avatarWrap: { position: "relative", flex: "none" },
    avatar: { width: 64, height: 64, fontSize: 20 },
    avatarBadge: {
      position: "absolute",
      bottom: -2,
      right: -2,
      width: 24,
      height: 24,
      borderRadius: "50%",
      backgroundColor: theme.palette.aeros.surface2,
      border: `1px solid ${isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.15)"}`,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: theme.palette.text.secondary,
      "& .MuiSvgIcon-root": { fontSize: 13 },
    },
    ddi: { color: theme.palette.text.secondary, fontSize: 13, whiteSpace: "nowrap" },
    numberOk: { color: AEROS.success, fontSize: 15 },
    walletAvatar: { width: 18, height: 18, fontSize: 8, fontWeight: 800 },
    tagsBox: {
      backgroundColor: theme.palette.aeros.surface,
      border: `1px solid ${borderInput}`,
      borderRadius: 10,
      padding: "7px 10px",
      display: "flex",
      gap: 6,
      flexWrap: "wrap",
      alignItems: "center",
      minHeight: 40,
      "&:focus-within": {
        borderColor: AEROS.cyan,
        boxShadow: "0 0 0 3px rgba(34,211,238,0.15)",
      },
    },
    tagInput: {
      flex: 1,
      minWidth: 120,
      "& .MuiInputBase-root": { fontSize: 12.5 },
      "& .MuiInputBase-input": { padding: "3px 0" },
      "& input::placeholder": { color: theme.palette.aeros.textTertiary, opacity: 1 },
    },
    extraRow: {
      display: "grid",
      gridTemplateColumns: "1fr 1.6fr 28px",
      gap: 8,
      padding: "8px 14px",
      borderTop: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.06)"}`,
      alignItems: "center",
    },
    extraEmpty: {
      padding: "12px 14px",
      fontSize: 12,
      color: theme.palette.aeros.textTertiary,
      borderTop: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.06)"}`,
    },
    extraDelete: {
      padding: 4,
      color: theme.palette.aeros.textTertiary,
      "& .MuiSvgIcon-root": { fontSize: 16 },
      "&:hover": { color: AEROS.error, backgroundColor: "transparent" },
    },
    toggles: { display: "flex", gap: 10, flexWrap: "wrap", "& > *": { flex: "1 1 160px" }, [theme.breakpoints.down("xs")]: { flexDirection: "column" } },
    meta: {
      fontSize: 11,
      color: theme.palette.aeros.textTertiary,
      display: "flex",
      gap: 12,
      flexWrap: "wrap",
    },
    tagChipPurple: {
      color: stateText(theme, "#C08BFF", "purple"),
      backgroundColor: "rgba(160,68,255,0.12)",
    },
  };
});

const formatDateForInput = (date) => {
  if (!date) return "";
  if (typeof date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(date)) return date;
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const parseDateFromInput = (dateString) => {
  if (!dateString) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) return dateString;
  if (dateString.includes("T")) return dateString.split("T")[0];
  return dateString;
};

const ContactSchema = Yup.object().shape({
  name: Yup.string().min(2, "Muito curto").max(250, "Muito longo").required("Obrigatório"),
  number: Yup.string().min(8, "Muito curto").max(50, "Muito longo"),
  email: Yup.string().email("E-mail inválido"),
});

const withAlpha = (hex, a) => {
  if (!hex || !hex.startsWith("#")) return `rgba(160,68,255,${a})`;
  const s = hex.replace("#", "").slice(0, 6);
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(s.slice(i, i + 2), 16));
  return `rgba(${r},${g},${b},${a})`;
};

const ContactModal = ({ open, onClose, contactId, initialValues, onSave }) => {
  const classes = useStyles();
  const m = useAerosModalStyles();
  const aeros = useAerosSettingsStyles();
  const isMounted = useRef(true);
  const { user } = useContext(AuthContext);

  const initialState = {
    name: "",
    number: "",
    email: "",
    disableBot: false,
    lgpdAcceptedAt: "",
    birthDate: "",
    extraInfo: [],
  };

  const [contact, setContact] = useState(initialState);
  const [disableBot, setDisableBot] = useState(false);
  const [acceptAudio, setAcceptAudio] = useState(true);
  const [acceptCampaigns, setAcceptCampaigns] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedQueue, setSelectedQueue] = useState("");
  const [queues, setQueues] = useState([]);
  const [users, setUsers] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState("");
  const [numberFocus, setNumberFocus] = useState(false);
  const [blockOpen, setBlockOpen] = useState(false);
  const [blocking, setBlocking] = useState(false);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Atendentes (carteira) e tags carregados uma vez por abertura
  useEffect(() => {
    if (!open) return;
    api
      .get("/users/list", { params: { companyId: user.companyId } })
      .then(({ data }) => setUsers(Array.isArray(data) ? data : []))
      .catch((err) => toastError(err));
    api
      .get("/tags/list", { params: { kanban: 0 } })
      .then(({ data }) => setAllTags(Array.isArray(data) ? data : []))
      .catch((err) => toastError(err));
  }, [open, user.companyId]);

  useEffect(() => {
    const fetchContact = async () => {
      if (initialValues) {
        setContact((prev) => ({
          ...prev,
          ...initialValues,
          birthDate: formatDateForInput(initialValues.birthDate),
        }));
      }
      if (!contactId) {
        setTags([]);
        setDisableBot(false);
        setAcceptAudio(true);
        setAcceptCampaigns(true);
        setSelectedUser(null);
        setSelectedQueue("");
        setQueues([]);
        return;
      }
      try {
        const { data } = await api.get(`/contacts/${contactId}`);
        if (!isMounted.current) return;
        setContact({ ...data, birthDate: formatDateForInput(data.birthDate) });
        setDisableBot(Boolean(data.disableBot));
        setAcceptAudio(data.acceptAudioMessage !== false);
        setAcceptCampaigns(data.acceptCampaigns !== false);
        setTags(Array.isArray(data.tags) ? data.tags : []);
        if (data.contactWallets && data.contactWallets.length > 0) {
          const wallet = data.contactWallets[0].wallet;
          const queue = data.contactWallets[0].queue;
          setSelectedUser(wallet ? { id: wallet.id, name: wallet.name } : null);
          setSelectedQueue(queue ? queue.id : "");
          setQueues(queue ? [{ id: queue.id, name: queue.name }] : []);
        } else {
          setSelectedUser(null);
          setSelectedQueue("");
          setQueues([]);
        }
      } catch (err) {
        toastError(err);
      }
    };
    if (open) fetchContact();
  }, [contactId, open, initialValues]);

  const handleClose = () => {
    onClose();
    setContact(initialState);
    setSelectedUser(null);
    setSelectedQueue("");
    setQueues([]);
    setTags([]);
    setTagInput("");
  };

  const syncTags = async (id, list) => {
    await api.post("/tags/sync", { contactId: id, tags: list });
  };

  const handleSaveContact = async (values) => {
    try {
      const contactData = {
        ...values,
        disableBot,
        acceptAudioMessage: acceptAudio,
        acceptCampaigns,
        birthDate: parseDateFromInput(values.birthDate),
      };
      delete contactData.contactWallets;
      delete contactData.tags;

      let id = contactId;
      if (contactId) {
        if (!selectedUser || !selectedQueue) {
          await api.delete(`/contacts/wallet/${contactId}`);
        }
        await api.put(`/contacts/${contactId}`, contactData);
      } else {
        const { data } = await api.post("/contacts", contactData);
        id = data.id;
        if (onSave) onSave(data);
      }

      if (id && selectedUser && selectedQueue) {
        await api.put(`/contacts/wallet/${id}`, {
          wallets: { userId: selectedUser.id, queueId: selectedQueue },
        });
      }
      if (id) {
        await syncTags(id, tags);
      }

      toast.success(i18n.t("contactModal.success"));
      handleClose();
    } catch (err) {
      toastError(err);
    }
  };

  const handleBlock = async () => {
    setBlocking(true);
    try {
      await api.put(`/contacts/block/${contactId}`, { active: !contact.active });
      toast.success(contact.active ? i18n.t("contacts.toasts.blocked") : i18n.t("contacts.toasts.unblocked"));
      setContact((prev) => ({ ...prev, active: !prev.active }));
      setBlockOpen(false);
    } catch (err) {
      toastError(err);
    } finally {
      setBlocking(false);
    }
  };

  const addTag = async (tag) => {
    if (!tag) return;
    if (tags.some((t) => t.id === tag.id)) return;
    setTags([...tags, tag]);
    setTagInput("");
  };

  const createTag = async (name) => {
    const trimmed = String(name || "").trim();
    if (!trimmed) return;
    const existing = allTags.find((t) => t.name.toLowerCase() === trimmed.toLowerCase());
    if (existing) return addTag(existing);
    try {
      const colors = AEROS.avatarCycle;
      const { data } = await api.post("/tags", {
        name: trimmed,
        color: colors[Math.floor(Math.random() * colors.length)],
      });
      setAllTags([...allTags, data]);
      addTag(data);
    } catch (err) {
      toastError(err);
    }
  };

  const tagOptions = allTags.filter((t) => !tags.some((s) => s.id === t.id));
  const isEdit = Boolean(contactId);
  const avatarColor = avatarColorFor(contactId || 0);
  const picture = contact?.urlPicture && !/nopicture\.png$/i.test(contact.urlPicture) ? contact.urlPicture : null;

  return (
    <>
      <AerosConfirmModal
        open={blockOpen}
        onClose={() => setBlockOpen(false)}
        onConfirm={handleBlock}
        loading={blocking}
        icon={contact.active ? <BlockIcon /> : <CheckCircleOutlineIcon />}
        tone={contact.active ? "danger" : "info"}
        primary={!contact.active}
        title={
          contact.active
            ? i18n.t("contacts.confirm.blockTitle", { name: contact.name })
            : i18n.t("contacts.confirm.unblockTitle", { name: contact.name })
        }
        description={contact.active ? i18n.t("contacts.confirm.blockText") : i18n.t("contacts.confirm.unblockText")}
        confirmLabel={contact.active ? i18n.t("contacts.confirm.block") : i18n.t("contacts.confirm.unblock")}
      />
      <Dialog open={open} onClose={handleClose} maxWidth={false} classes={{ paper: classes.paper }} scroll="paper">
        <Formik
          initialValues={contact}
          enableReinitialize={true}
          validationSchema={ContactSchema}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSaveContact(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({ values, errors, touched, isSubmitting, setFieldValue }) => (
            <Form className={m.form}>
              <div className={m.header}>
                <div>
                  <div className={m.title}>
                    {isEdit ? i18n.t("contactModal.title.edit") : i18n.t("contactModal.title.add")}
                  </div>
                  <div className={m.subtitle}>
                    {isEdit
                      ? `#${contactId}${contact.createdAt ? ` · ${i18n.t("contactModal.createdAt")} ${format(new Date(contact.createdAt), "dd/MM/yyyy")}` : ""}`
                      : i18n.t("contactModal.subtitleAdd")}
                  </div>
                </div>
                <IconButton className={m.closeButton} onClick={handleClose} aria-label={i18n.t("contactModal.buttons.cancel")}>
                  <CloseIcon />
                </IconButton>
              </div>

              <div className={m.body}>
                <div className={classes.topRow}>
                  <div className={classes.avatarWrap}>
                    <div className={`${m.avatar} ${classes.avatar}`} style={{ backgroundColor: avatarColor }}>
                      {picture ? <img src={picture} alt={contact.name} /> : initialsOf(values.name || contact.name)}
                    </div>
                    <div className={classes.avatarBadge} title={i18n.t("contactModal.photoHint")}>
                      <PhotoCameraOutlinedIcon />
                    </div>
                  </div>
                  <div className={m.grid2} style={{ flex: 1 }}>
                    <div>
                      <label className={m.fieldLabel} htmlFor="contact-name">
                        {i18n.t("contactModal.form.name")}
                      </label>
                      <Field
                        as={TextField}
                        id="contact-name"
                        name="name"
                        autoFocus
                        variant="outlined"
                        className={m.input}
                        error={touched.name && Boolean(errors.name)}
                        helperText={touched.name && errors.name}
                      />
                    </div>
                    <div>
                      <label className={`${m.fieldLabel} ${numberFocus ? m.fieldLabelFocus : ""}`} htmlFor="contact-number">
                        WhatsApp
                      </label>
                      <Field
                        as={TextField}
                        id="contact-number"
                        name="number"
                        variant="outlined"
                        className={m.input}
                        placeholder="5511984321187"
                        onFocus={() => setNumberFocus(true)}
                        onBlur={() => setNumberFocus(false)}
                        error={touched.number && Boolean(errors.number)}
                        helperText={touched.number && errors.number}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <span className={classes.ddi}>🇧🇷</span>
                            </InputAdornment>
                          ),
                          endAdornment:
                            String(values.number || "").replace(/\D/g, "").length >= 12 ? (
                              <InputAdornment position="end">
                                <CheckCircleIcon className={classes.numberOk} />
                              </InputAdornment>
                            ) : null,
                        }}
                      />
                    </div>
                  </div>
                </div>

                <div className={m.grid2}>
                  <div>
                    <label className={m.fieldLabel} htmlFor="contact-email">
                      {i18n.t("contactModal.form.email")}
                    </label>
                    <Field
                      as={TextField}
                      id="contact-email"
                      name="email"
                      variant="outlined"
                      className={m.input}
                      placeholder="email@exemplo.com"
                      error={touched.email && Boolean(errors.email)}
                      helperText={touched.email && errors.email}
                    />
                  </div>
                  <div>
                    <label className={m.fieldLabel}>{i18n.t("contactModal.form.wallet")}</label>
                    <Autocomplete
                      options={users}
                      value={selectedUser}
                      getOptionLabel={(o) => o?.name || ""}
                      getOptionSelected={(o, v) => o?.id === v?.id}
                      popupIcon={<ExpandMoreIcon />}
                      onChange={(e, newValue) => {
                        setSelectedUser(newValue);
                        if (newValue && Array.isArray(newValue.queues) && newValue.queues.length) {
                          setQueues(newValue.queues);
                          setSelectedQueue(newValue.queues.length === 1 ? newValue.queues[0].id : "");
                        } else {
                          setQueues([]);
                          setSelectedQueue("");
                        }
                      }}
                      renderOption={(option) => (
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                          <span
                            className={`${m.avatar} ${classes.walletAvatar}`}
                            style={{ backgroundColor: avatarColorFor(option.id) }}
                          >
                            {initialsOf(option.name)}
                          </span>
                          {option.name}
                        </span>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          className={m.input}
                          placeholder={i18n.t("contactModal.form.walletPlaceholder")}
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: selectedUser ? (
                              <InputAdornment position="start">
                                <span
                                  className={`${m.avatar} ${classes.walletAvatar}`}
                                  style={{ backgroundColor: avatarColorFor(selectedUser.id) }}
                                >
                                  {initialsOf(selectedUser.name)}
                                </span>
                              </InputAdornment>
                            ) : null,
                          }}
                        />
                      )}
                    />
                  </div>
                </div>

                <div className={m.grid2}>
                  <div>
                    <label className={m.fieldLabel}>{i18n.t("contactModal.form.walletQueue")}</label>
                    <TextField
                      select
                      variant="outlined"
                      className={m.input}
                      value={selectedQueue || ""}
                      onChange={(e) => setSelectedQueue(e.target.value)}
                      disabled={!selectedUser}
                      SelectProps={{ IconComponent: ExpandMoreIcon, displayEmpty: true }}
                    >
                      <MenuItem value="">
                        <em>{i18n.t("contactModal.form.walletQueuePlaceholder")}</em>
                      </MenuItem>
                      {queues.map((q) => (
                        <MenuItem key={q.id} value={q.id}>
                          {q.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </div>
                  <div>
                    <label className={m.fieldLabel} htmlFor="contact-birth">
                      {i18n.t("contactModal.form.birthDate")}
                    </label>
                    <Field
                      as={TextField}
                      id="contact-birth"
                      name="birthDate"
                      type="date"
                      variant="outlined"
                      className={m.input}
                      onChange={(e) => setFieldValue("birthDate", parseDateFromInput(e.target.value))}
                    />
                  </div>
                </div>

                <div>
                  <label className={m.fieldLabel}>{i18n.t("contactModal.form.tags")}</label>
                  <div className={classes.tagsBox}>
                    {tags.map((tag) => (
                      <span
                        key={tag.id}
                        className={m.chip}
                        style={{
                          color: stateText({ palette: { type: "dark" } }, tag.color || "#C08BFF"),
                          backgroundColor: withAlpha(tag.color || "#A044FF", 0.14),
                        }}
                      >
                        {tag.name}
                        <CloseIcon onClick={() => setTags(tags.filter((t) => t.id !== tag.id))} />
                      </span>
                    ))}
                    <Autocomplete
                      className={classes.tagInput}
                      options={tagOptions}
                      inputValue={tagInput}
                      onInputChange={(e, v) => setTagInput(v)}
                      value={null}
                      freeSolo
                      getOptionLabel={(o) => (typeof o === "string" ? o : o.name)}
                      onChange={(e, v) => {
                        if (typeof v === "string") createTag(v);
                        else if (v) addTag(v);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="standard"
                          placeholder={i18n.t("contactModal.form.tagsPlaceholder")}
                          InputProps={{ ...params.InputProps, disableUnderline: true }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && tagInput.trim()) {
                              e.preventDefault();
                              createTag(tagInput);
                            }
                          }}
                        />
                      )}
                    />
                  </div>
                </div>

                <FieldArray name="extraInfo">
                  {({ push, remove }) => (
                    <div className={m.section}>
                      <div className={m.sectionHead}>
                        {i18n.t("contactModal.form.extraInfo")}
                        <button type="button" className={m.linkButton} onClick={() => push({ name: "", value: "" })}>
                          <AddIcon /> {i18n.t("contactModal.buttons.addField")}
                        </button>
                      </div>
                      {(!values.extraInfo || values.extraInfo.length === 0) && (
                        <div className={classes.extraEmpty}>{i18n.t("contactModal.form.extraEmpty")}</div>
                      )}
                      {values.extraInfo &&
                        values.extraInfo.map((info, index) => (
                          <div className={classes.extraRow} key={`${index}-info`}>
                            <Field
                              as={TextField}
                              name={`extraInfo[${index}].name`}
                              variant="outlined"
                              className={`${m.input} ${m.inputSmall}`}
                              placeholder={i18n.t("contactModal.form.extraName")}
                            />
                            <Field
                              as={TextField}
                              name={`extraInfo[${index}].value`}
                              variant="outlined"
                              className={`${m.input} ${m.inputSmall}`}
                              placeholder={i18n.t("contactModal.form.extraValue")}
                            />
                            <IconButton className={classes.extraDelete} onClick={() => remove(index)} aria-label="Remover">
                              <DeleteOutlineIcon />
                            </IconButton>
                          </div>
                        ))}
                    </div>
                  )}
                </FieldArray>

                <div className={classes.toggles}>
                  <div className={m.optionBox} style={{ flex: 1 }}>
                    <div style={{ flex: 1 }}>
                      <div className={m.optionTitle}>{i18n.t("contactModal.form.chatbotTitle")}</div>
                      <div className={m.optionDesc}>
                        {disableBot ? i18n.t("contactModal.form.chatbotOff") : i18n.t("contactModal.form.chatbotOn")}
                      </div>
                    </div>
                    <Switch
                      className={aeros.toggleSwitchSmall}
                      checked={disableBot}
                      onChange={(e) => setDisableBot(e.target.checked)}
                      color="primary"
                    />
                  </div>
                  <div className={m.optionBox} style={{ flex: 1 }}>
                    <div style={{ flex: 1 }}>
                      <div className={m.optionTitle}>{i18n.t("contactModal.form.campaignsTitle")}</div>
                      <div className={m.optionDesc}>{i18n.t("contactModal.form.campaignsDesc")}</div>
                    </div>
                    <Switch
                      className={aeros.toggleSwitchSmall}
                      checked={acceptCampaigns}
                      onChange={(e) => setAcceptCampaigns(e.target.checked)}
                      color="primary"
                    />
                  </div>
                  <div className={m.optionBox} style={{ flex: 1 }}>
                    <div style={{ flex: 1 }}>
                      <div className={m.optionTitle}>{i18n.t("contactModal.form.audioTitle")}</div>
                      <div className={m.optionDesc}>{i18n.t("contactModal.form.audioDesc")}</div>
                    </div>
                    <Switch
                      className={aeros.toggleSwitchSmall}
                      checked={acceptAudio}
                      onChange={(e) => setAcceptAudio(e.target.checked)}
                      color="primary"
                    />
                  </div>
                </div>

                {isEdit && (
                  <div className={classes.meta}>
                    {contact.whatsapp?.name && (
                      <span>
                        {i18n.t("contactModal.form.whatsapp")} {contact.whatsapp.name}
                      </span>
                    )}
                    {contact.lid && <span>LID {contact.lid}</span>}
                    {contact.lgpdAcceptedAt && (
                      <span>
                        {i18n.t("contactModal.form.termsLGDP")}{" "}
                        {format(new Date(contact.lgpdAcceptedAt), "dd/MM/yyyy 'às' HH:mm")}
                      </span>
                    )}
                    {contact.number && <span>{formatPhone(contact.number)}</span>}
                  </div>
                )}
              </div>

              <div className={m.footer}>
                {isEdit && (
                  <button type="button" className={m.dangerLink} onClick={() => setBlockOpen(true)}>
                    {contact.active ? <BlockIcon /> : <CheckCircleOutlineIcon />}
                    {contact.active ? i18n.t("contactModal.buttons.block") : i18n.t("contactModal.buttons.unblock")}
                  </button>
                )}
                <div className={m.footerActions}>
                  <Button onClick={handleClose} disabled={isSubmitting} variant="outlined" className={m.cancelButton}>
                    {i18n.t("contactModal.buttons.cancel")}
                  </Button>
                  <Button type="submit" color="primary" disabled={isSubmitting} variant="contained" className={m.submitButton}>
                    {isEdit ? i18n.t("contactModal.buttons.okEdit") : i18n.t("contactModal.buttons.okAdd")}
                    {isSubmitting && <CircularProgress size={24} className={m.buttonProgress} />}
                  </Button>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </Dialog>
    </>
  );
};

export default ContactModal;
